/**
 * Análise das pendências que travam o lançamento mensal da carteira:
 * fundos sem `fund_local_quotas.json_quotas` e fundos sem `fund_local_quotas.json_fund_infos`
 * para o mês/ano selecionado.
 *
 * A análise roda inteiramente sobre o `assetsFormatted` que a tela de Coleta já tem em
 * memória — nenhuma requisição nova. Os campos usados vêm do SELECT de
 * getClientAllAssetsByDateAndRegime (src/controllers/fund.controller.js) e o
 * `dados_cadastrais` é anexado ao ativo por getLocalInfosByListFunds
 * (client/src/controllers/FundLocalQuotasController.js), que faz spread do
 * JSON.parse(json_fund_infos) — logo `dados_cadastrais` presente <=> json_fund_infos
 * preenchido no mês.
 */

/**
 * Um ativo de fundo tem cotas do mês quando possui pelo menos uma cota não replicada.
 * Mesma condição usada pela validação pré-lançamento em BalancesCollectScreen
 * (`noQuotasNow`). `quotas_anbima` já chega parseado como array por toJsonQuotasAssets.
 *
 * NÃO adicione aqui um teste de `valor_cota > 0`. Parece a correção óbvia para as séries que
 * estão gravadas com todas as cotas zeradas, mas em fundo com cota por cliente o `valor_cota`
 * default é justamente 0/placeholder e o valor real vive em `valor_cota_<client_id>` — exigir
 * valor positivo colocaria esses fundos na lista de "sem cotas" e a atualização em massa
 * tentaria substituir uma cota digitada à mão, que não existe na Comdinheiro. Se algum dia isso
 * mudar, `hasClientSpecificQuota` abaixo continua sendo a trava. Ver
 * COTA_POR_CLIENTE_FUNDO_SPECIAL.md.
 *
 * @param {Object} asset linha de fundo de assetsFormatted
 * @returns {boolean}
 */
export function hasFundQuotas(asset) {

    if (!asset || !Array.isArray(asset.quotas_anbima)) return false;

    return asset.quotas_anbima.filter(quota => quota && !quota.replicated).length > 0;
}

/**
 * O fundo tem cota específica de algum cliente no mês (chave `valor_cota_<client_id>`).
 *
 * Esse dado é digitado à mão e não é recuperável da Comdinheiro, então o fundo fica fora da
 * atualização em massa de cotas independentemente da flag `special` — existem fundos com cota
 * por cliente que NÃO estão marcados como especiais.
 *
 * `getValueQuota` (FundLocalQuotasController) preserva a chave ao sobrescrever `valor_cota`,
 * então ela continua visível no objeto em memória.
 *
 * @param {Object} asset linha de fundo de assetsFormatted
 * @returns {boolean}
 */
export function hasClientSpecificQuota(asset) {

    if (!asset || !Array.isArray(asset.quotas_anbima)) return false;

    return asset.quotas_anbima.some(quota => quota
        && Object.keys(quota).some(key => /^valor_cota_\d+$/.test(key)));
}

/**
 * Só interessam as linhas de FUNDO: assetsFormatted também carrega as linhas-rótulo de
 * segmento/artigo (labelSegment/labelArticle) e as linhas de TÍTULO (title_id), que não
 * têm nada em fund_local_quotas.
 */
function isFundRow(row) {

    return !!row
        && !row.labelSegment
        && !row.labelArticle
        && !row.title_id
        && !!row.fund_id;
}

/**
 * Campos mínimos que as atualizações em massa precisam. `suffix`, `subclass`,
 * `validity_subclass` e `ticker` são o que getCodeToComdinheiroQuotas usa para montar o
 * código do fundo na URL da Comdinheiro; `fund_local_quota_id` diz se já existe linha em
 * fund_local_quotas no mês (o update de dados cadastrais é UPDATE puro).
 */
function toPendencyFund(asset) {

    return {
        fund_id: asset.fund_id,
        fund_name: asset.fund_name,
        cnpj: asset.cnpj,
        suffix: asset.suffix,
        subclass: asset.subclass,
        validity_subclass: asset.validity_subclass,
        ticker: asset.ticker,
        fund_local_quota_id: asset.fund_local_quota_id,
    };
}

/**
 * Fundos do cliente sem cotas, sem dados cadastrais e/ou com cota replicada no mês.
 *
 * Descartes:
 * - cotas: fundos `special` não trazem cotas da API (a cota é digitada em
 *   NewSpecialClientQuotaModal), então nunca são pendência de cota; e fundos com cota
 *   específica de cliente no mês, que também é digitada à mão (ver hasClientSpecificQuota);
 * - dados cadastrais: fundos marcados com `no_infos_api` declaradamente não possuem
 *   dados cadastrais na Comdinheiro.
 *
 * Cotas replicadas são apenas AVISO, nunca impedimento: o lançamento segue normalmente com a
 * última cota conhecida repetida no dia sem cotação (normalizeMissingQuotaValues em
 * AssetsController). Só entram fundos comuns — `replicatedQuotasDays` não é preenchido para
 * fundos special, cuja série replicada é o comportamento esperado todo mês.
 *
 * O mesmo fundo aparece uma vez por conta em assetsFormatted; aqui é deduplicado por
 * fund_id.
 *
 * calendarDivergence vem pronto do AssetsController (getQuotasModel): dias em que o fundo usado
 * como modelo para replicar as cotas dos fundos especiais não bate com os dias úteis do mês. Também
 * é só aviso: o modelo é completado com os dias úteis e o lançamento segue.
 *
 * @param {Array<Object>} assetsFormatted
 * @param {Object} [calendarDivergence] quotasModelDivergence devolvido por getAssetInfos
 * @returns {{fundsNoQuotas: Array<Object>, fundsNoInfos: Array<Object>, fundsReplicatedQuotas: Array<Object>, calendarDivergence: Object|null}}
 */
export function analyzeFundsPendencies(assetsFormatted, calendarDivergence) {

    const fundsNoQuotas = [];
    const fundsNoInfos = [];
    const fundsReplicatedQuotas = [];

    const seenNoQuotas = new Set();
    const seenNoInfos = new Set();
    const seenReplicated = new Set();

    (assetsFormatted || []).filter(isFundRow).forEach(asset => {

        if (!asset.special
            && !hasClientSpecificQuota(asset)
            && !hasFundQuotas(asset)
            && !seenNoQuotas.has(asset.fund_id)) {

            seenNoQuotas.add(asset.fund_id);
            fundsNoQuotas.push(toPendencyFund(asset));
        }

        if (!asset.no_infos_api && !asset.dados_cadastrais && !seenNoInfos.has(asset.fund_id)) {

            seenNoInfos.add(asset.fund_id);
            fundsNoInfos.push(toPendencyFund(asset));
        }

        if (Array.isArray(asset.replicatedQuotasDays)
            && asset.replicatedQuotasDays.length > 0
            && !seenReplicated.has(asset.fund_id)) {

            seenReplicated.add(asset.fund_id);
            fundsReplicatedQuotas.push({
                ...toPendencyFund(asset),
                replicatedDays: asset.replicatedQuotasDays,
            });
        }
    });

    return {
        fundsNoQuotas,
        fundsNoInfos,
        fundsReplicatedQuotas,
        calendarDivergence: assetsFormatted && calendarDivergence ? calendarDivergence : null,
    };
}

/**
 * Total de pendências encontradas.
 *
 * @param {Object} analysis retorno de analyzeFundsPendencies
 * @returns {number}
 */
export function countFundsPendenciesIssues(analysis) {

    if (!analysis) return 0;

    return (analysis.fundsNoQuotas?.length ?? 0)
        + (analysis.fundsNoInfos?.length ?? 0)
        + (analysis.fundsReplicatedQuotas?.length ?? 0)
        + (analysis.calendarDivergence ? 1 : 0);
}

/**
 * Converte uma lista crua de linhas de `assetsFormatted` (por exemplo o `noQuotasBefore` do
 * launchWallet) na lista que pode ser enviada para doBulkUpdateQuotasByListFunds.
 *
 * Descarta o que não tem cota buscável na Comdinheiro, com os mesmos critérios de
 * analyzeFundsPendencies: isFundRow tira as linhas-rótulo e as de TÍTULO (que também entram em
 * `noQuotasBefore`, mas não têm nada em fund_local_quotas), `special` e cota específica de cliente
 * são digitadas à mão. Esses ativos continuam listados como pendência para tratamento manual.
 *
 * hasClientSpecificQuota é avaliado sobre `quotas_anbima` (mês selecionado) de propósito: quando a
 * pendência é do mês anterior, `quotas_anbima_before` está vazio e não haveria chave
 * `valor_cota_<client_id>` para encontrar — o mês atual é o único sinal disponível de que aquele
 * fundo tem cota por cliente.
 *
 * @param {Array<Object>} rows linhas de assetsFormatted
 * @returns {Array<Object>} fundos deduplicados por fund_id
 */
export function selectFundsToBulkQuotasUpdate(rows) {

    const funds = [];
    const seen = new Set();

    (rows || []).filter(isFundRow).forEach(asset => {

        if (asset.special || hasClientSpecificQuota(asset) || seen.has(asset.fund_id)) return;

        seen.add(asset.fund_id);

        //fund_local_quota_id fica de fora: ele é o id da linha do MÊS SELECIONADO, e esta lista
        //pode se referir a outro mês. doBulkUpdateQuotasByListFunds não usa o campo, mas
        //doBulkUpdateInfosByListFunds usa para decidir se já existe linha no mês — mandar o id
        //errado faria o update de dados cadastrais acreditar numa linha que não existe.
        const fund = toPendencyFund(asset);
        delete fund.fund_local_quota_id;

        funds.push(fund);
    });

    return funds;
}
